import React, { useMemo, useEffect, useRef } from 'react';
import { SignIn, useAuth } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../styles/AuthPage.module.css';
import { maybeTrackAuthCtaFromAuthPage, trackAuthPageViewed } from '../analytics';
import { isGameplayAccessReason } from '../utils/gameplayAccess';
import useAuthReturnPath from '../hooks/useAuthReturnPath';

const POST_AUTH_LOGIN_EVENT_HINT_KEY = 'post-auth-login-event-hint';
const POST_AUTH_LOGIN_EVENT_PENDING_KEY = 'post-auth-login-event-pending';
const POST_AUTH_LOGIN_EVENT_PENDING_WINDOW_MS = 1 * 60 * 1000;
const MONDIALI_STADIUM_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/images/mondiali_bg.jpeg`;

/**
 * Simple skeleton placeholder that matches the form size
 */
const AuthFormSkeleton = () => (
  <div className={styles.skeleton} />
);

const AuthContentLayout = ({ hideNav, onBack, children }) => {
  if (hideNav) {
    return children;
  }
  return <PageShell onBack={onBack} showStickyAdRail={false}>{children}</PageShell>;
};

/**
 * Custom Sign In Page
 * 
 * Uses Clerk's SignIn component embedded in our own page layout.
 * Supports a `next` query parameter for post-authentication redirection.
 * Localization is handled automatically via ClerkProvider in index.js.
 */
const SignInPage = () => {
  const hasRedirectedRef = useRef(false);
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const hasFreshPendingLoginMarker = React.useCallback(() => {
    try {
      const raw = sessionStorage.getItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      const createdAtMs = Number(parsed?.created_at_ms);
      if (!Number.isFinite(createdAtMs)) {
        sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
        return false;
      }
      const ageMs = Date.now() - createdAtMs;
      if (ageMs < 0 || ageMs > POST_AUTH_LOGIN_EVENT_PENDING_WINDOW_MS) {
        sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
        return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const consumePendingLoginMarker = React.useCallback(() => {
    const hasFreshMarker = hasFreshPendingLoginMarker();
    let marker = null;
    try { marker = hasFreshMarker ? JSON.parse(sessionStorage.getItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY)) : null; } catch (_) {}
    try {
      sessionStorage.removeItem(POST_AUTH_LOGIN_EVENT_PENDING_KEY);
    } catch (_) {}
    return marker;
  }, [hasFreshPendingLoginMarker]);

  // Extract and validate query parameters
  const { nextParam, clerkRedirectParam, message, sourceParam, gateReasonParam } = useMemo(() => {
    let next = null;
    let msg = null;
    let source = null;
    let gateReason = null;
    let clerkRedirect = null;
    try {
      const params = new URLSearchParams(location.search);
      const nextVal = params.get('next');
      clerkRedirect = params.get('redirect_url');
      const shouldClearMessage = params.get('clear_message') === '1';
      // Only allow internal paths (starting with / but not //)
      if (nextVal && nextVal.startsWith('/') && !nextVal.startsWith('//')) {
        next = nextVal;
      }
      // Get optional message to display
      msg = shouldClearMessage ? null : (params.get('message') || null);
      source = params.get('source') || null;
      gateReason = params.get('gate_reason') || null;
    } catch (_) {}
    return { nextParam: next, clerkRedirectParam: clerkRedirect, message: msg, sourceParam: source, gateReasonParam: gateReason };
  }, [location.search]);
  const isMondialiFlow = sourceParam === 'mondiali';
  const containerClassName = `${styles.container} ${isMondialiFlow ? styles.mondialiFlowBackground : ''}`.trim();
  const mondialiBackgroundStyle = isMondialiFlow
    ? { '--mondiali-stadium-image': `url("${MONDIALI_STADIUM_IMAGE_URL}")` }
    : undefined;

  const { afterAuthUrl, forceRedirectUrl } = useAuthReturnPath(nextParam, isSignedIn, clerkRedirectParam);

  // Build signUpUrl preserving next and message params.
  // Fall back to afterAuthUrl so cross-auth links keep redirect intent
  // even when current URL params were stripped.
  const signUpUrl = useMemo(() => {
    const params = new URLSearchParams();
    const redirectNext = nextParam || (afterAuthUrl !== '/' ? afterAuthUrl : null);
    if (redirectNext) {
      params.set('next', redirectNext);
      params.set('redirect_url', redirectNext);
    }
    if (message) params.set('message', message);
    if (sourceParam) params.set('source', sourceParam);
    if (gateReasonParam) params.set('gate_reason', gateReasonParam);
    const qs = params.toString();
    return qs ? `/signup?${qs}` : '/signup';
  }, [nextParam, message, sourceParam, gateReasonParam, afterAuthUrl]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    maybeTrackAuthCtaFromAuthPage({
      source_surface: sourceParam || null,
      gate_reason: gateReasonParam || null,
      cta_kind: 'sign_in',
      next_path: afterAuthUrl,
    });
  }, [afterAuthUrl, gateReasonParam, isLoaded, isSignedIn, sourceParam]);

  useEffect(() => {
    trackAuthPageViewed({
      auth_mode: 'sign_in',
      source_surface: sourceParam || 'auth_page_direct',
      gate_reason: gateReasonParam || null,
      next_path: afterAuthUrl,
    });
  }, [afterAuthUrl, sourceParam, gateReasonParam]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    if (hasFreshPendingLoginMarker()) return;
    try {
      sessionStorage.setItem(
        POST_AUTH_LOGIN_EVENT_PENDING_KEY,
        JSON.stringify({
          reason: 'interactive_sign_in',
          created_at_ms: Date.now(),
        }),
      );
    } catch (_) {}
  }, [hasFreshPendingLoginMarker, isLoaded, isSignedIn]);

  // Handle redirect after successful sign-in
  useEffect(() => {
    if (isLoaded && isSignedIn && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      const pendingMarker = consumePendingLoginMarker();
      if (pendingMarker) {
        try {
          sessionStorage.setItem(
            POST_AUTH_LOGIN_EVENT_HINT_KEY,
            JSON.stringify({
              reason: 'interactive_sign_in',
              started_at_ms: pendingMarker.created_at_ms,
              created_at_ms: Date.now(),
            }),
          );
        } catch (_) {}
      }
      try {
        sessionStorage.removeItem('auth-redirect-next');
      } catch (_) {}
      navigate(afterAuthUrl, { replace: true });
    }
  }, [consumePendingLoginMarker, isLoaded, isSignedIn, afterAuthUrl, navigate]);

  // Show skeleton while Clerk is loading
  if (!isLoaded) {
    return (
      <AuthContentLayout hideNav={isMondialiFlow} onBack={() => navigate(-1)}>
        <div className={containerClassName} style={mondialiBackgroundStyle}>
          <div className={styles.clerkWrapper}>
            <AuthFormSkeleton />
          </div>
        </div>
      </AuthContentLayout>
    );
  }

  // If already signed in, the useEffect above will handle the redirect
  if (isSignedIn) {
    return (
      <AuthContentLayout hideNav={isMondialiFlow}>
        <div className={containerClassName} style={mondialiBackgroundStyle}>
          <div className={styles.clerkWrapper}>
            <AuthFormSkeleton />
          </div>
        </div>
      </AuthContentLayout>
    );
  }

  return (
    <AuthContentLayout hideNav={isMondialiFlow} onBack={() => navigate(-1)}>
      <div className={containerClassName} style={mondialiBackgroundStyle}>
        <div className={styles.clerkWrapper}>
          {message && (
            <div className={styles.messageBox}>
              <p className={styles.messageText}>{message}</p>
            </div>
          )}
          <SignIn 
            routing="path"
            path="/signin"
            signUpUrl={signUpUrl}
            fallbackRedirectUrl={afterAuthUrl}
            forceRedirectUrl={forceRedirectUrl}
            signUpFallbackRedirectUrl={afterAuthUrl}
            signUpForceRedirectUrl={forceRedirectUrl}
            appearance={{
              elements: {
                rootBox: styles.clerkRoot,
                card: styles.clerkCard,
                headerTitle: styles.clerkCardHeader,
                headerSubtitle: styles.clerkCardHeader,
                socialButtonsBlockButton: styles.clerkSocialButton,
                formFieldLabel: styles.clerkFormFieldLabel,
                formFieldLabelRow: styles.clerkFormFieldLabelRow,
                formFieldInput: styles.clerkFormFieldInput,
                formFieldHintText: styles.clerkFormFieldHintText,
                formFieldErrorText: styles.clerkFormFieldErrorText,
                formFieldSuccessText: styles.clerkFormFieldSuccessText,
                identityPreview: styles.clerkIdentityPreview,
                identityPreviewText: styles.clerkIdentityPreviewText,
                identityPreviewEditButton: styles.clerkIdentityPreviewEditButton,
                formButtonPrimary: styles.clerkFormButtonPrimary,
                footer: styles.clerkFooter,
                footerActionLink: styles.clerkFooterActionLink,
                dividerLine: styles.clerkDividerLine,
                dividerText: styles.clerkDividerText,
                logoBox: styles.clerkLogoBox,
                main: styles.clerkMain,
              }
            }}
          />
        </div>
      </div>
    </AuthContentLayout>
  );
};

export default SignInPage;
